import {FC, useMemo} from 'react';
import {Formik, Form} from 'formik';
import * as Yup from 'yup';
import {toastr} from '@/utils/toastHelper';
import {Col, Row} from "react-bootstrap";
import {FormPageFooter, FormPageHeader} from "@components/falcon/common/forms";
import {BaseInfoBlock, RewardBlock} from "@components/consumer/daily/forms";
import {BalanceGiftInfo, ItemGiftInfo, PatchDailyGiftModel} from "@models/consumer/daily";
import {EGiftType} from "@interfaces/user/daily";
import ConsumerDailyService from "@api/consumer/consumerDailyService";
import {IConsumerGift} from "@interfaces/consumer/daily";

interface EditDailyGiftFormProps {
    gift: IConsumerGift;
}

interface EditDailyGiftFormValues {
    name: string;
    days: number;
    isActive: boolean;
    repeat: boolean;
    oneTime: boolean;
    balance?: number;
    itemId?: string;
    count?: number;
    type: EGiftType;
    groupId: string | null;
}

const EditDailyGiftForm: FC<EditDailyGiftFormProps> = ({gift}) => {

    const validationSchema = useMemo(() => Yup.object({
        name: Yup.string()
            .min(3)
            .max(100)
            .required(),
        days: Yup.number()
            .min(1)
            .max(1000)
            .required(),
        isActive: Yup.boolean()
            .required(),
        repeat: Yup.boolean()
            .required(),
        oneTime: Yup.boolean()
            .required(),
        type: Yup.mixed<EGiftType>()
            .oneOf(Object.values(EGiftType))
            .required(),
        balance: Yup.number()
            .when('type', {
                is: EGiftType.BalanceGift,
                then: schema => schema
                    .max(10000000)
                    .required()
            }),
        itemId: Yup.string()
            .when('type', {
                is: EGiftType.ItemGift,
                then: schema => schema
                    .required()
            }),
        count: Yup.number()
            .when('type', {
                is: EGiftType.ItemGift,
                then: schema => schema
                    .min(1)
                    .max(2000000000)
                    .required()
            }),
        groupId: Yup.string()
            .nullable()
            .notRequired()
    }), []);

    const initialValues: EditDailyGiftFormValues = {
        name: gift.name,
        days: gift.days,
        isActive: gift.isActive,
        repeat: gift.repeat,
        oneTime: gift.oneTime,
        type: gift.giftType,
        balance: (gift.giftInfo as BalanceGiftInfo).balance || 0,
        itemId: (gift.giftInfo as ItemGiftInfo).itemId || '',
        count: (gift.giftInfo as ItemGiftInfo).count || 0,
        groupId: gift.groupId,
    };

    const onSubmit = async (values: EditDailyGiftFormValues) => {
        const info = values.type === EGiftType.BalanceGift ? {
            balance: values.balance || 0
        } as BalanceGiftInfo : {
            itemId: values.itemId || '',
            count: values.count || 0
        } as ItemGiftInfo;

        const model: PatchDailyGiftModel = {
            id: gift.id,
            name: values.name,
            days: values.days,
            isActive: values.isActive,
            repeat: values.repeat,
            oneTime: values.oneTime,
            groupId: values.groupId,
            info
        };

        const api = new ConsumerDailyService();
        const result = values.type === EGiftType.BalanceGift ?
            await api.patchBalanceGift(model) :
            await api.patchItemGift(model);

        if (result.success) {
            toastr.success("Подарки", "Подарок успешно обновлен");
        }
        else {
            if (result.errorCode === 'DisabledItem') {
                toastr.error("Подарки", "Данный предмет недоступен");
            }
            else if (result.errorCode === 'InvalidGiftType') {
                toastr.error("Подарки", "Неизвестный тип подарка");
            }
            else {
                toastr.error("Подарки", "Что-то пошло не так");
            }
        }
    }

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
        >
            <Form>
                <Row className="g-3">
                    <Col xs={12}>
                        <FormPageHeader title={`Редактирование подарка "${gift.name}"`} />
                    </Col>
                    <Col lg={8}>
                        <BaseInfoBlock editForm />
                    </Col>
                    <Col lg={4} className="sticky-sidebar">
                        <RewardBlock editForm={true}/>
                    </Col>
                    <Col>
                        <FormPageFooter cancelUrl={`/consumer/daily/`} />
                    </Col>
                </Row>
            </Form>
        </Formik>
    )
}

export default EditDailyGiftForm;
